import { ArrowRight, Bell, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { triggerDjenIngest, type ComunicacaoProcessualItem } from '../../api/client';
import type { DjenProgress } from '../../hooks/useDjenProgress';
import { ComunicacaoSheet } from '../../components/comunicacoes/ComunicacaoSheet';
import { ComunicacaoListItem } from '../../components/comunicacoes/ComunicacaoListItem';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { cn } from '../../lib/utils';
import { formatRelativeTime, getSyncStatusColor } from './helpers';

// ---------------------------------------------------------------------------
// Intimações Recentes
// ---------------------------------------------------------------------------
export function RecentComunicacoes({
  comunicacoes,
  loading,
}: {
  comunicacoes?: ComunicacaoProcessualItem[];
  loading?: boolean;
}) {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <>
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Intimações Recentes</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary h-auto py-1 px-2"
              onClick={() => navigate('/app/comunicacoes')}
            >
              Ver todas
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>

          <div className="space-y-1">
            {loading ? (
              <div className="flex justify-center py-6">
                <Spinner size={32} />
              </div>
            ) : comunicacoes && comunicacoes.length > 0 ? (
              comunicacoes.map((com) => <ComunicacaoListItem key={com.id} com={com} onSelect={setSelectedId} compact />)
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">Nenhuma intimação encontrada</p>
            )}
          </div>
        </CardContent>
      </Card>

      <ComunicacaoSheet comunicacaoId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sincronizar com o DJEN (topo do painel, à esquerda da busca)
// ---------------------------------------------------------------------------
const syncStatusLabels: Record<string, string> = {
  PENDING: 'Pendente',
  RUNNING: 'Em execução',
  COMPLETED: 'Concluído',
  FAILED: 'Falhou',
};

export function DjenSyncButton({
  ultimoSync,
  ultimoStatus,
  djenProgress,
  onSyncTriggered,
}: {
  ultimoSync: string | null;
  ultimoStatus: string | null;
  djenProgress?: DjenProgress | null;
  onSyncTriggered?: () => void;
}) {
  const [syncActive, setSyncActive] = useState(false);
  const userTriggeredRef = useRef(false);
  const isRunning = djenProgress?.status === 'RUNNING' || djenProgress?.status === 'PENDING';
  const isFinished = djenProgress?.status === 'COMPLETED' || djenProgress?.status === 'FAILED';
  const justCompleted = djenProgress?.status === 'COMPLETED';
  const showSyncing = syncActive || isRunning;

  // Status otimista: quando COMPLETED chega do SSE, mostrar "Atualizado — agora"
  // sem esperar o refetch de dashStats
  const syncInfo = justCompleted
    ? { bg: 'bg-emerald-500/10', color: 'text-emerald-500', label: 'Atualizado' }
    : getSyncStatusColor(ultimoSync);

  // Render-time: detectar conclusão do job e limpar syncActive (padrão React 18)
  if (syncActive && isFinished) {
    setSyncActive(false);
  }

  // Toast só quando o USUÁRIO disparou o sync (não em reconexão SSE ao voltar à página)
  const completedShownRef = useRef(false);
  const failedShownRef = useRef(false);
  useEffect(() => {
    if (djenProgress?.status === 'COMPLETED' && userTriggeredRef.current && !completedShownRef.current) {
      completedShownRef.current = true;
      userTriggeredRef.current = false;
      const c = djenProgress.comunicacoes ?? 0;
      const p = djenProgress.processos ?? 0;
      toast.success(
        c > 0
          ? `Sincronização concluída — ${c} ${c === 1 ? 'intimação' : 'intimações'} em ${p} ${p === 1 ? 'processo' : 'processos'}`
          : 'Sincronização concluída',
      );
    }
    if (djenProgress?.status !== 'COMPLETED') {
      completedShownRef.current = false;
    }
    if (djenProgress?.status === 'FAILED' && userTriggeredRef.current && !failedShownRef.current) {
      failedShownRef.current = true;
      userTriggeredRef.current = false;
      toast.error(djenProgress.error || 'Falha ao sincronizar com o DJE');
    }
    if (djenProgress?.status !== 'FAILED') {
      failedShownRef.current = false;
    }
  }, [djenProgress?.status, djenProgress?.comunicacoes, djenProgress?.processos, djenProgress?.error]);

  // Timeout: se SSE não reportar conclusão em 60s, parar spinner
  useEffect(() => {
    if (!syncActive) return;
    const timer = setTimeout(() => setSyncActive(false), 60_000);
    return () => clearTimeout(timer);
  }, [syncActive]);

  const handleSync = async () => {
    userTriggeredRef.current = true;
    setSyncActive(true);
    onSyncTriggered?.();
    try {
      await triggerDjenIngest();
    } catch {
      setSyncActive(false);
      userTriggeredRef.current = false;
    }
  };

  const ultimo = justCompleted
    ? 'Atualizado agora'
    : ultimoSync
      ? `${syncInfo.label}: ${formatRelativeTime(ultimoSync)}`
      : syncInfo.label;
  const status =
    ultimoStatus && !justCompleted ? ` · último status: ${syncStatusLabels[ultimoStatus] ?? ultimoStatus}` : '';
  const progresso =
    djenProgress?.status === 'RUNNING' && djenProgress.pct != null
      ? `Buscando… ${djenProgress.pct}%`
      : 'Sincronizando…';

  return (
    <Button
      variant="outline"
      className="min-h-11 shrink-0 gap-2 px-3"
      onClick={handleSync}
      disabled={showSyncing}
      title={`Diário de Justiça Eletrônico Nacional — ${ultimo}${status}`}
      aria-label={
        showSyncing ? progresso : `Sincronizar DJEN (Diário de Justiça Eletrônico Nacional). ${ultimo}${status}`
      }
    >
      {showSyncing ? (
        <Spinner size={16} className="text-inherit" />
      ) : (
        <span className={cn('relative flex h-4 w-4 items-center justify-center', syncInfo.color)}>
          <RefreshCw className="h-4 w-4 text-foreground" />
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-background"
            style={{ backgroundColor: 'currentColor' }}
          />
        </span>
      )}
      {/* No celular, só o ícone: a busca ao lado precisa da largura. */}
      <span className={showSyncing ? undefined : 'hidden sm:inline'}>
        {showSyncing ? progresso : 'Sincronizar DJEN'}
      </span>
    </Button>
  );
}
